import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout, { useViewport, TopBar } from '../../components/Layout';
import { Meta, Display } from '../../components/Atoms';

/**
 * ScheduleShell - wspólny layout dla huba "Harmonogram roślin".
 *
 * Sekcje:
 *   /garden-tasks            -> Kwitnienie (default)
 *   /garden-tasks/seasonal   -> Zabiegi sezonowe (zima + dzielenie bylin)
 *   /garden-tasks/routine    -> Pielęgnacja bieżąca (deadheading + sukcesja)
 */

const SECTIONS = [
  { key: 'bloom',    to: '/garden-tasks',          label: 'Kwitnienie' },
  { key: 'seasonal', to: '/garden-tasks/seasonal', label: 'Zabiegi sezonowe' },
  { key: 'routine',  to: '/garden-tasks/routine',  label: 'Pielęgnacja bieżąca' },
];

function activeKey(pathname) {
  if (pathname === '/garden-tasks' || pathname === '/garden-tasks/') return 'bloom';
  if (pathname.startsWith('/garden-tasks/seasonal')) return 'seasonal';
  if (pathname.startsWith('/garden-tasks/routine')) return 'routine';
  return 'bloom';
}

function ScheduleSidebar({ active }) {
  return (
    <aside style={{
      width: 240,
      flexShrink: 0,
      borderRight: '1px solid var(--t-line)',
      padding: '32px 0',
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start',
      maxHeight: '100vh',
      overflowY: 'auto',
    }}>
      <div style={{ padding: '0 24px 24px' }}>
        <Meta>Harmonogram</Meta>
      </div>
      <nav style={{ padding: '0 12px' }}>
        {SECTIONS.map(s => {
          const on = s.key === active;
          return (
            <Link
              key={s.key}
              to={s.to}
              style={{
                display: 'block', padding: '12px 14px',
                borderRadius: 9, marginBottom: 2,
                background: on ? 'rgba(168, 196, 158, 0.10)' : 'transparent',
                color: on ? 'var(--t-text)' : 'var(--t-text-dim)',
                fontFamily: 'var(--t-sans)', fontSize: 14,
                fontWeight: on ? 500 : 400,
                letterSpacing: '-.01em',
                textDecoration: 'none',
                transition: 'background 200ms ease',
              }}
            >{s.label}</Link>
          );
        })}
      </nav>
    </aside>
  );
}

export default function ScheduleShell({ children, title, subtitle, eyebrow }) {
  const location = useLocation();
  const active = activeKey(location.pathname);
  const { isPhone, isTablet } = useViewport();

  if (isPhone || isTablet) {
    return (
      <Layout>
        <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 20 }}>
          <div style={{
            padding: '0 20px 16px',
            display: 'flex', gap: 14, flexWrap: 'wrap',
            overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          }}>
            {SECTIONS.map(s => {
              const on = s.key === active;
              return (
                <Link
                  key={s.key}
                  to={s.to}
                  style={{
                    fontFamily: 'var(--t-mono)', fontSize: 11,
                    letterSpacing: '.18em', textTransform: 'uppercase',
                    color: on ? 'var(--t-sage)' : 'var(--t-text-mute)',
                    textDecoration: 'none',
                    borderBottom: on ? '1px solid var(--t-sage)' : 'none',
                    paddingBottom: 4, whiteSpace: 'nowrap',
                  }}
                >{s.label}</Link>
              );
            })}
          </div>
          <div style={{ padding: '0 24px 24px' }}>
            {eyebrow && <Meta color="sage" style={{ marginBottom: 8 }}>{eyebrow}</Meta>}
            {title && (
              <Display size={'clamp(32px, 7vw, 42px)'} style={{
                display: 'block', letterSpacing: '-.025em', lineHeight: 0.95,
                marginBottom: subtitle ? 12 : 24,
              }}>{title}</Display>
            )}
            {subtitle && (
              <div style={{
                fontSize: 14, color: 'var(--t-text-dim)',
                lineHeight: 1.55, marginBottom: 24,
              }}>{subtitle}</div>
            )}
          </div>
          <div style={{ padding: '0 24px 60px' }}>{children}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout topBar={<TopBar
      left={<Meta>Harmonogram · {SECTIONS.find(s => s.key === active)?.label || ''}</Meta>}
    />}>
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
        <ScheduleSidebar active={active} />
        <div style={{ flex: 1, padding: '40px 48px 60px', maxWidth: 980 }}>
          {eyebrow && <Meta color="sage" style={{ marginBottom: 10 }}>{eyebrow}</Meta>}
          {title && (
            <Display size={44} style={{
              display: 'block', letterSpacing: '-.03em',
              marginBottom: subtitle ? 14 : 28,
            }}>{title}</Display>
          )}
          {subtitle && (
            <div style={{
              fontSize: 15, color: 'var(--t-text-dim)',
              lineHeight: 1.55, marginBottom: 28, maxWidth: 600,
            }}>{subtitle}</div>
          )}
          {children}
        </div>
      </div>
    </Layout>
  );
}

export function ScheduleSection({ title, description, children, action }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{
        paddingBottom: 12, marginBottom: 18,
        borderBottom: '1px solid var(--t-line-2)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 14,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Display size={20} style={{ display: 'block', marginBottom: description ? 6 : 0 }}>
            {title}
          </Display>
          {description && (
            <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>
              {description}
            </div>
          )}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}
